import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_AUTHENTICATED_REDIRECT = '/dashboard';
const GUARDED_PATHS = ['/login', '/register', '/reset-password'];

/**
 * Only honour a `redirect` target that is a same-origin absolute path.
 * Rejects absolute URLs (https://...), protocol-relative URLs (//host),
 * backslash-smuggled variants, and targets that point back at a guarded auth
 * page (which would otherwise loop).
 */
export const resolveSafeRedirect = (redirect: string | null): string => {
  if (!redirect) return DEFAULT_AUTHENTICATED_REDIRECT;
  if (!redirect.startsWith('/')) return DEFAULT_AUTHENTICATED_REDIRECT;
  if (redirect.startsWith('//') || redirect.startsWith('/\\')) {
    return DEFAULT_AUTHENTICATED_REDIRECT;
  }
  const pathname = redirect.split(/[?#]/)[0];
  if (GUARDED_PATHS.includes(pathname)) {
    return DEFAULT_AUTHENTICATED_REDIRECT;
  }
  return redirect;
};

interface PublicOnlyRouteProps {
  children: React.ReactNode;
}

export const PublicOnlyRoute: React.FC<PublicOnlyRouteProps> = ({
  children,
}) => {
  const { loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTarget = resolveSafeRedirect(
    new URLSearchParams(location.search).get('redirect'),
  );

  useEffect(() => {
    if (loading) return;

    if (isAuthenticated) {
      navigate(redirectTarget, { replace: true });
    }
  }, [loading, isAuthenticated, redirectTarget, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

export default PublicOnlyRoute;
